import { normalizeInvoiceAppointmentTimes } from "./invoiceAppointmentTimes";

const decimalFromSaleMoney = (money) => {
  if (money && typeof money === "object") {
    const amount = Number(money.amount_minor);
    return Number.isFinite(amount) ? amount / 100 : undefined;
  }
  if (money === null || money === undefined || money === "") return undefined;
  const amount = Number(money);
  return Number.isFinite(amount) ? amount : undefined;
};

const normalizeInvoiceMoneyField = (value, fallback = 0) => {
  const amount = decimalFromSaleMoney(value);
  if (amount === undefined) return fallback;
  return value && typeof value === "object" ? amount : value;
};

const typeName = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const typeNames = {
    product: "Product",
    service: "Service",
    appointment: "Appointment",
    addonservice: "AddOnServices",
    addonservices: "AddOnServices",
    addanotherservice: "AddAnotherServices",
    addanotherservices: "AddAnotherServices",
    subscription: "Subscription",
    membership: "Membership",
    voucher: "Voucher",
    oneoffvoucher: "OneOffVoucher",
    "one-off-voucher": "OneOffVoucher",
  };
  return typeNames[normalized.toLowerCase()]
    || normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const normalizeInvoiceCartLine = (line) => {
  if (!line || typeof line !== "object") return line;

  const existingType = line.type;
  const type = typeName(existingType || line.item?.type);
  const lineTotal = decimalFromSaleMoney(line.line_total);
  const quantity = line.qty ?? line.quantity ?? 1;
  const quantityNumber = Number(quantity);
  const unitAmount = decimalFromSaleMoney(line.unit_amount)
    ?? (lineTotal !== undefined && Number.isFinite(quantityNumber) && quantityNumber > 0
      ? lineTotal / quantityNumber
      : lineTotal);
  const cost = decimalFromSaleMoney(line.cost) ?? unitAmount ?? lineTotal ?? 0;
  const normalized = {
    ...line,
    ...(type ? { type } : {}),
    cost,
    qty: quantity,
  };

  if (type === "Product") {
    normalized.products = line.products || { name: line.name || "" };
    normalized.staff = line.staff || {};
  } else if (type === "Service") {
    normalized.services = line.services || { name: line.name || "" };
    normalized.staff = line.staff || {};
  } else if (["Appointment", "AddOnServices", "AddAnotherServices"].includes(type)) {
    normalized.services = line.services || { name: line.name || "" };
    normalized.staff = line.staff || {};
    normalized.appointment = line.appointment || {};
  } else if (type === "Subscription") {
    normalized.subscription = line.subscription || { name: line.name || "" };
    normalized.clientsubscriptionplan = line.clientsubscriptionplan || [];
    normalized.staff = line.staff || {};
  } else if (type === "Membership") {
    normalized.membership = line.membership || { name: line.name || "" };
  } else if (type === "Voucher" || type === "OneOffVoucher") {
    const voucherToCandidate = line.voucherto || line.voucher_to;
    const voucherTo = voucherToCandidate && typeof voucherToCandidate === "object"
      ? voucherToCandidate
      : {};
    normalized.voucherto = {
      ...voucherTo,
      code: voucherTo.code || "-",
      voucher: voucherTo.voucher || { name: line.name || "" },
    };
  }

  return normalized;
};

const normalizeInvoicePayment = (payment) => {
  if (!payment || typeof payment !== "object") return payment;
  const amount = decimalFromSaleMoney(payment.amount);
  return {
    ...payment,
    ...(amount === undefined ? {} : { amount }),
    ...(payment.amount && typeof payment.amount === "object" && Object.prototype.hasOwnProperty.call(payment.amount, "currency") ? {currency: payment.amount.currency} : {}),
    paidby: payment.paidby === undefined ? payment.paid_by : payment.paidby,
  };
};

export const normalizeSaleInvoiceRow = (row) => {
  if (!row || typeof row !== "object") return row;

  const totalprice = row.totalprice === undefined ? decimalFromSaleMoney(row.total) : row.totalprice;
  return {
    ...row,
    invoicedate: row.invoicedate === undefined ? row.invoice_date : row.invoicedate,
    ...(totalprice === undefined ? {} : { totalprice }),
  };
};

export const getSaleInvoiceTotal = (detail) => {
  if (!detail || typeof detail !== "object") return 0;

  for (const candidate of [detail.total_pay, detail.total, detail.totalprice]) {
    const amount = decimalFromSaleMoney(candidate);
    if (amount !== undefined) return amount;
  }

  return 0;
};

export const normalizeSaleInvoiceDetail = (detail) => {
  if (!detail || typeof detail !== "object") return detail;
  detail = normalizeInvoiceAppointmentTimes(detail);

  const total = decimalFromSaleMoney(detail.total);
  const payments = Array.isArray(detail.payments)
    ? detail.payments.map(normalizeInvoicePayment)
    : detail.payments;
  const lastpayment = Array.isArray(detail.lastpayment)
    ? detail.lastpayment.map(normalizeInvoicePayment)
    : [];
  const cart = Array.isArray(detail.cart) ? detail.cart.map((line) => {
    const normalizedLine = normalizeInvoiceCartLine(line);
    if (!["Appointment", "AddOnServices", "AddAnotherServices"].includes(normalizedLine?.type)) return normalizedLine;
    const appointment = line.appointment;
    if (!appointment) return normalizedLine;
    return {
      ...normalizedLine,
      appointment,
      staff: normalizedLine.staff && Object.keys(normalizedLine.staff).length ? normalizedLine.staff : appointment.staff || {},
      services: normalizedLine.services?.name ? normalizedLine.services : appointment.service || { name: line.name || "" },
      start_datetime: normalizedLine.start_datetime || appointment.start_datetime,
      end_datetime: normalizedLine.end_datetime || appointment.end_datetime,
    };
  }) : undefined;
  const normalized = {
    ...detail,
    invoicedate: detail.invoicedate === undefined ? detail.invoice_date : detail.invoicedate,
    ...(detail.currency === undefined && detail.total && detail.total.currency
      ? { currency: detail.total.currency }
      : {}),
    total_pay: detail.total_pay === undefined
      ? (total ?? 0)
      : normalizeInvoiceMoneyField(detail.total_pay),
    totalprice: detail.totalprice === undefined
      ? (total ?? 0)
      : normalizeInvoiceMoneyField(detail.totalprice),
    ...(cart ? { cart } : {}),
    ...(detail.line_items === undefined && cart ? { line_items: cart.map((line) => ({
      ...(line.id ? { id: line.id } : {}),
      name: line.name || line.services?.name || line.products?.name || "",
      quantity: Number(line.qty || line.quantity || 1),
      unit_amount: { amount_minor: Math.round((decimalFromSaleMoney(line.cost) || 0) * 100) },
    })) } : {}),
    ...(Array.isArray(detail.payments) ? { payments } : {}),
    lastpayment: detail.lastpayment === undefined
      ? (Array.isArray(payments) ? payments : [])
      : lastpayment,
    ...(detail.appliedvoucherto === undefined && detail.applied_voucher !== undefined
      ? { appliedvoucherto: detail.applied_voucher }
      : {}),
  };

  return normalized;
};

export const normalizeSaleInvoiceResponse = (response) => {
  if (Array.isArray(response)) return response.map(normalizeSaleInvoiceRow);
  if (!response || typeof response !== "object" || !Array.isArray(response.data)) return response;

  return { ...response, data: response.data.map(normalizeSaleInvoiceRow) };
};
